import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { split } from 'lodash';
import { of, Subscription } from 'rxjs';
import { UcrSecurityService } from '../../core/ucr-security/ucr-security.service';
import { ButtonConfig } from '../../forms/button/button.types';
import {
    DateInputType,
    DateRangeInputType,
    InputType,
    QuestionDefinition,
    QuestionValueChange,
    SelectInputType
} from '../../forms/form.types';
import { QuestionService } from '../../forms/question/question.service';
import { AlertService } from '../../shared/services/alert.service';
import { ExportStatus, SelectListOption } from '../../shared/shared.types';
import { ReflectionService } from '../../shared/utilities/reflection.service';
import { ExportConfiguration, ExportConfigurationInput, TableState, TableStateService } from '../table-state.service';
import {
    FilterInputType,
    FilterSearchCriteria,
    PagingSort,
    TableFilter,
    TableFilterControl,
    TableFilterItem
} from '../table.types';
import { FilterStateService } from './filter-state.service';

@Component({
    selector: 'app-table-filter',
    templateUrl: './table-filter.component.html',
    styleUrls: ['./table-filter.component.scss'],
    encapsulation: ViewEncapsulation.Emulated
})
export class TableFilterComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() filterItems: Array<TableFilterItem>;
    @Input() tableName: string;
    @Input() initialFilter: FilterSearchCriteria;
    @Input() wideRow: boolean;
    @Input() mayExport: boolean;
    @Input() trackExport: boolean;
    @Input() compactButtons: boolean;
    @Input() exportConfigurationInput: ExportConfigurationInput;
    @ViewChild('searchButton', { read: ElementRef }) searchButton: ElementRef;
    private exportTimeOut: number;
    searchButtonTooltip = 'Click to Search.';
    hasLessCommon: boolean;
    private criteriaChanges$: Subscription;
    private pageIndex: number;
    private pageSize: number;
    private sortBy: string;
    private sortDirection: string;
    private tempFilterUserName: string;
    get filtersMatch(): boolean {
        return this.filterOutput === this.lastFilterOutputAfterSearch;
    }

    loading: boolean = true;
    dataLoaded: boolean;
    filtersChanged: boolean;
    controls: TableFilterControl;
    filterOutput: string;
    lastFilterOutputAfterSearch: string;
    private stateSubscription: Subscription;
    mmddyyyy = 'MM/dd/yyyy';
    questions: {
        [k: string]: QuestionDefinition;
    } = {};

    buttonConfig: ButtonConfig = {
        styles: {
            'background-color': '#ffd740',
            color: '#000000'
        }
    };

    storedFilterQuestion: QuestionDefinition;

    dateOperatorSelectList: Array<SelectListOption> = [
        { text: 'Greater than', value: 'gt' },
        { text: 'Less than', value: 'lt' },
        { text: 'Not equal to', value: 'ne' },
        { text: 'Equal to', value: 'eq' }
    ];
    notImmediateLoad: boolean;
    private filterStateList: Array<FilterSearchCriteria> = [];
    selectedFilter: FilterSearchCriteria;
    currentExportStatus: string;
    private doneExporting: boolean;
    private currentExportId: string;
    constructor(
        private reflection: ReflectionService,
        private tableState: TableStateService,
        private datePipe: DatePipe,
        private questionService: QuestionService,
        private http: HttpClient,
        private alert: AlertService,
        private filterStateService: FilterStateService,
        private renderer2: Renderer2,
        private changeDet: ChangeDetectorRef,
        private security: UcrSecurityService
    ) {
        this.security.getUserInfo().subscribe((ui) => {
            this.tempFilterUserName = this.reflection.isNotNullOrEmpty(ui?.username) ? ui.username : '';
        });
        this.subscribe();
    }

    ngOnInit(): void {
        if (this.filterItems) {
            this.hasLessCommon = this.filterItems.filter((m) => m.lessCommon === true)?.length > 0;
            this.controls = {};
            for (const item of this.filterItems) {
                this.controls[item.property] = new UntypedFormControl(item.defaultValue);
                switch (item.inputType) {
                    case 'input':
                        break;
                    case 'input-range':
                        break;
                    case 'select':
                        break;
                    case 'multiselect':
                        break;
                    case 'date':
                        this.controls[this.dateOperator(item)] = new UntypedFormControl('');
                        break;
                    case 'date-range':
                        this.controls[this.endDateProperty(item)] = new UntypedFormControl(null);
                        break;
                }
            }
            this.convertToQuestions();
            this.addLessCommon();
        }
        if (this.initialFilter) {
            this.getInitialFilters();
        }
        this.subCriteriaChanges();
    }

    ngAfterViewInit() {
        const fs: string = sessionStorage.getItem(`${this.tableName}_${this.tempFilterUserName}_temp_filters`);
        if (this.reflection.isNotNullOrEmpty(fs)) {
            this.selectedFilter = JSON.parse(fs);
            this.processFilterChange(false);
            this.filterOutput = this.getFilterOutput();
            this.lastFilterOutputAfterSearch = this.getFilterOutput();
            this.filtersChanged = false;
            this.addRemoveDirtyClass(false);
            this.changeDet.detectChanges();
        }
    }

    ngOnDestroy() {
        if (this.stateSubscription) {
            this.stateSubscription.unsubscribe();
        }
        if (this.criteriaChanges$) {
            this.criteriaChanges$.unsubscribe();
        }
    }

    private addLessCommon() {
        if (this.hasLessCommon) {
            this.controls.lessCommon = new UntypedFormControl(false);
            this.questions.lessCommon = {
                input: {
                    discriminator: 'CHECKBOX',
                    property: 'lessCommon',
                    control: this.controls.lessCommon,
                    label: 'Show additional filters'
                }
            };
        }
    }

    private convertToQuestions(): void {
        if (this.filterItems) {
            for (const k in this.filterItems) {
                const item = this.filterItems[k];
                const input: InputType = {
                    discriminator: undefined,
                    property: item.property,
                    control: this.controls[item.property],
                    label: this.computeLabel(item),
                    placeholder: this.computePlaceHolder(item.inputType),
                    appearance: 'legacy',
                    floatLabel: 'always'
                };
                switch (item.inputType) {
                    case 'input':
                    case 'input-range':
                        this.questions[item.property] = {
                            input: this.questionService.toText(input)
                        };
                        break;
                    case 'select':
                    case 'multiselect':
                        const select: SelectInputType = this.questionService.toSelect(input);
                        select.options$ = item.selectOptions;
                        select.clearButton = item.clearButton;
                        select.isMultiple = item.inputType === 'multiselect';
                        select.selectedValuesAsInfo = true;
                        this.questions[item.property] = {
                            input: select
                        };
                        break;
                    case 'date':
                        const date: DateInputType = this.questionService.toDate(input);
                        date.noHint = true;
                        this.questions[item.property] = { input: date };
                        this.questions[this.dateOperator(item)] = { input: this.createDateOperator(item) };
                        break;
                    case 'date-range':
                        const dateRange: DateRangeInputType = this.questionService.toDateRange(input);
                        dateRange.noHint = true;
                        dateRange.endDateFormControl = this.controls[this.endDateProperty(item)];
                        this.questions[item.property] = { input: dateRange };
                        break;
                }
            }
        }
        this.lastFilterOutputAfterSearch = this.getFilterOutput();
        this.renderFilterOutput();
        this.filtersChanged = false;
    }

    private computePlaceHolder(type: FilterInputType) {
        switch (type) {
            case 'input':
                return 'Enter text to search by';
            case 'input-range':
                return 'Enter text or create a range with ~';
            case 'date':
                return 'Select a date';
            case 'date-range':
                return 'Select a date or a range';
            case 'multiselect':
                return 'Select one or more...';
            case 'select':
                return 'Select one...';
        }
    }

    dateOperator(item: TableFilterItem): string {
        return `${item.property}Operator`;
    }

    private endDateProperty(item: TableFilterItem): string {
        return `${item.property}EndDate`;
    }

    private dateOperatorText(value: string): string {
        return this.dateOperatorSelectList.find((m) => m.value === value)?.text;
    }

    private subscribe() {
        if (this.tableState) {
            this.stateSubscription = this.tableState.fromTableResponse.subscribe((state: TableState) => {
                if (this.tableName === state.tableName) {
                    this.loading = state.loading;
                    this.dataLoaded = state.hasData;
                    this.notImmediateLoad = state.notImmediateLoad;
                    this.doneExporting = state.doneExporting;
                    if (state.updateSelect) {
                        this.updateSelectQuestions();
                    }
                }
            });
        }
    }

    private updateSelectQuestions() {
        for (const key of Object.keys(this.questions)) {
            const question = this.questions[key];
            if (question.input.discriminator === 'SELECT') {
                (question.input as SelectInputType).updateOptions = true;
            }
        }
    }

    private computeLabel(item: TableFilterItem) {
        if (item && item.label) {
            return item.label;
        }
        return this.reflection.toTitleCase(item.property);
    }

    private getFilterOutput(): string {
        const arr: Array<string> = [];
        for (const item of this.filterItems) {
            if (item.skipInFilterOut === true) {
                continue;
            }
            let value = this.controls[item.property]?.value;
            const label = this.computeLabel(item);
            if (!this.reflection.isNotNullOrEmpty(value) && item.inputType !== 'date-range') {
                continue;
            }
            switch (item.inputType) {
                case 'input':
                    value = `contains ${value}`;
                    break;
                case 'input-range':
                    if (/~/.test(value)) {
                        let splitValues: string[] = split(value, /(~)/);
                        splitValues = splitValues.filter((m) => m !== '');
                        if (splitValues.length === 2) {
                            if (splitValues[0] === '~') {
                                value = `before ${splitValues[1]} [inclusive]`;
                            } else {
                                value = `after ${splitValues[0]} [inclusive]`;
                            }
                        } else if (splitValues.length === 3) {
                            value = `between ${splitValues[0]} and ${splitValues[2]} [inclusive]`;
                        } else {
                            value = "this does not look like a valid range modifier try 'a~z'";
                        }
                    } else {
                        value = `contains ${value}`;
                    }
                    break;
                case 'select':
                    value = `equal to ${item?.selectedOption?.text}`;
                    break;
                case 'multiselect':
                    value = `equal to ${this.reflection.toEnglishSentence(
                        item.selectedOption?.text?.split(','),
                        'or',
                        true
                    )}`;
                    break;
                case 'date-range':
                    const endDate = this.controls[this.endDateProperty(item)]?.value;
                    if (value && endDate) {
                        value = `between ${this.datePipe.transform(value, this.mmddyyyy)} and ${this.datePipe.transform(
                            endDate,
                            this.mmddyyyy
                        )}`;
                    } else if (endDate && !value) {
                        value = `less than ${this.datePipe.transform(endDate, this.mmddyyyy)}`;
                    } else if (value) {
                        value = `greater than ${this.datePipe.transform(value, this.mmddyyyy)}`;
                    } else {
                        continue;
                    }
                    break;
                case 'date':
                    const op = this.controls[this.dateOperator(item)]?.value;
                    if (this.reflection.isNotNullOrEmpty(op)) {
                        value = `${this.dateOperatorText(op).toLowerCase()} ${this.datePipe.transform(
                            value,
                            this.mmddyyyy
                        )}`;
                    } else {
                        value = '';
                    }
                    break;
            }

            if (this.reflection.isNotNullOrEmpty(value)) {
                arr.push(`${label} ${value}`);
            }
        }
        if (arr.length > 0) {
            return this.reflection.toEnglishSentence(arr, 'AND', true);
        } else {
            return '';
        }
    }

    filterUpdated($event: QuestionValueChange, question: QuestionDefinition) {
        this.selectionChange($event, question);
        this.renderFilterOutput();
        this.dataLoaded = false;
        this.filtersChanged = true;
        this.addRemoveDirtyClass(this.filtersChanged);
    }

    private renderFilterOutput() {
        const statement = this.getFilterOutput();
        if (this.reflection.isNotNullOrEmpty(statement)) {
            this.filterOutput = "Click 'Search' to update: " + statement;
        } else {
            this.filterOutput = '';
        }
    }

    private getFilter(): TableFilter {
        const filter: TableFilter = {};
        for (const c in this.controls) {
            filter[c] = this.controls[c].value;
        }
        return filter;
    }

    private filterSearchCriteriaInstance(): FilterSearchCriteria {
        const fs: FilterSearchCriteria = {
            alias: this.initialFilter?.alias,
            filters: [],
            controls: {}
        };
        return fs;
    }

    search() {
        const f: TableFilter = this.getFilter();
        let tempFilter = this.filterSearchCriteriaInstance();
        tempFilter = this.addControlsAndItems(tempFilter, true);
        sessionStorage.setItem(`${this.tableName}_${this.tempFilterUserName}_temp_filters`, JSON.stringify(tempFilter));
        this.tableState.sendFilter({
            tableName: this.tableName,
            filter: f,
            pagingSort: this.selectedFilter?.pagingSort ? this.selectedFilter.pagingSort : null
        });
        this.lastFilterOutputAfterSearch = this.getFilterOutput();
        this.dataLoaded = true;
        this.filtersChanged = false;
        this.addRemoveDirtyClass(this.filtersChanged);
    }

    resetFilters() {
        for (const filterItem of this.filterItems) {
            this.controls[filterItem.property].setValue(filterItem.defaultValue);
            if (filterItem.inputType === 'date-range') {
                this.controls[this.endDateProperty(filterItem)].setValue(filterItem.defaultValue);
            } else if (filterItem.inputType === 'date') {
                this.controls[this.dateOperator(filterItem)].setValue(null);
            }
        }
        if (this.controls.lessCommon) {
            this.controls.lessCommon.setValue(false);
        }

        this.filterUpdated(undefined, undefined);
        this.selectedFilter = null;
        if (this.storedFilterQuestion) {
            this.storedFilterQuestion.input.control.setValue(null);
        }
        this.search();
    }

    exportAsExcel() {
        this.currentExportId = `table_export_${new Date().getTime().toString()}`;
        const exportConfig: ExportConfiguration = this.trackExport
            ? {
                  exportId: this.currentExportId,
                  dataName: this.exportConfigurationInput?.dataName,
                  processingDataRowOf: this.exportConfigurationInput?.processingDataRowOf,
                  processingDataRows: this.exportConfigurationInput?.processingDataRows
              }
            : null;
        this.tableState.sendFilter({
            tableName: this.tableName,
            filter: this.getFilter(),
            export: true,
            exportConfig: exportConfig,
            filterStatementOutput: this.lastFilterOutputAfterSearch
        });
        this.doneExporting = false;
        this.trackExportDataUpdates();
    }

    private trackExportDataUpdates() {
        if (!this.trackExport) {
            return;
        }
        if (this.exportTimeOut) {
            clearTimeout(this.exportTimeOut);
        }
        this.exportTimeOut = setTimeout(() => {
            this.filterStateService
                .exportStatus(this.currentExportId)
                .then((value: ExportStatus) => {
                    this.currentExportStatus = value.exportStatus;
                    if (!this.doneExporting) {
                        this.trackExportDataUpdates();
                    }
                })
                .catch(() => {
                    this.currentExportStatus = '';
                });
        }, 300);
    }

    private selectionChange($event: QuestionValueChange, question: QuestionDefinition) {
        if (!$event || !$event?.inputValueChange?.payload) {
            return;
        }
        if ($event.inputValueChange.payload instanceof MatSelectChange) {
            const item = this.filterItems.find((m) => m.property === question.input.property);
            if (!item) {
                return;
            }
            item.selectedOption = {
                value: $event.inputValueChange.payload.value,
                text: $event.inputValueChange.payload.source.triggerValue
            };
        }
    }

    private createDateOperator(item: TableFilterItem): SelectInputType {
        const operator = this.dateOperator(item);
        if (!this.controls[operator]) {
            this.controls[operator] = new UntypedFormControl(null);
        }
        const input: SelectInputType = {
            discriminator: 'SELECT',
            label: this.computeLabel(item) + ' Operator',
            placeholder: 'Select an operator',
            property: this.dateOperator(item),
            control: this.controls[operator],
            appearance: 'legacy',
            floatLabel: 'always',
            options$: of(this.dateOperatorSelectList),
            clearButton: false
        };
        return input;
    }

    private getInitialFilters(): void {
        this.filterStateService
            .indexPromise({
                alias: this.initialFilter.alias
            })
            .then((value) => {
                this.filterStateList = value;
                if (value && value.length) {
                    this.createFilterSelectQuestion();
                }
            });
    }

    private createFilterSelectQuestion() {
        if (this.storedFilterQuestion) {
            return;
        }
        this.storedFilterQuestion = {
            input: {
                appearance: 'outline',
                discriminator: 'SELECT',
                property: 'storedFilter',
                label: 'Choose a stored filter to switch to',
                control: new UntypedFormControl(''),
                clearButton: true,
                options$: this.filterStateService.selectList({
                    q: this.initialFilter.alias
                })
            } as SelectInputType
        };
    }

    private addControlsAndItems(filter: FilterSearchCriteria, addPagingSort: boolean = false): FilterSearchCriteria {
        if (!filter) {
            return;
        }
        filter.filters = filter.filters ? filter.filters : [];
        filter.controls = filter.controls ? filter.controls : {};
        for (const c in this.controls) {
            this.reflection.setPropertyByString(filter.controls, c, this.controls[c].value);
        }
        for (const c in this.filterItems) {
            const item = this.filterItems[c];
            filter.filters.push({
                inputType: item.inputType,
                property: item.property,
                selectedOption: item.selectedOption
            });
        }
        if (addPagingSort) {
            filter.pagingSort = {
                tableName: this.tableName,
                pageIndex: this.pageIndex,
                pageSize: this.pageSize,
                sortBy: this.sortBy,
                sortDirection: this.sortDirection
            };
        }
        return filter;
    }

    private subCriteriaChanges() {
        this.criteriaChanges$ = this.tableState.fromTableCriteriaResponse.subscribe((v: PagingSort) => {
            if (v.tableName === this.tableName) {
                this.pageIndex = v.pageIndex;
                this.pageSize = v.pageSize;
                this.sortBy = v.sortBy;
                this.sortDirection = v.sortDirection;
                const fs: string = sessionStorage.getItem(`${this.tableName}_${this.tempFilterUserName}_temp_filters`);
                const f: FilterSearchCriteria = this.reflection.isNotNullOrEmpty(fs)
                    ? JSON.parse(fs)
                    : this.filterSearchCriteriaInstance();
                f.pagingSort = {
                    pageSize: v.pageSize,
                    pageIndex: v.pageIndex,
                    sortBy: v.sortBy,
                    sortDirection: v.sortDirection,
                    tableName: v.tableName
                };
                if (this.selectedFilter) {
                    this.selectedFilter.pagingSort = f.pagingSort;
                }
                sessionStorage.setItem(`${this.tableName}_${this.tempFilterUserName}_temp_filters`, JSON.stringify(f));
            }
        });
    }

    storeFilters() {
        this.alert
            .withRequiredTextInput(
                'Store new filter',
                'Please enter a unique filter name to store this new filter',
                'Filter name',
                'Save new filter'
            )
            .then((value) => {
                if (value.isConfirmed) {
                    const name = value.value;
                    if (!name) {
                        this.alert.toastError('Looks like we did not get the name you entered, please try again.');
                        return;
                    }
                    const existing = this.filterStateList?.find((m) => m.name === name);
                    if (existing) {
                        this.alert.toastError(
                            `A filter with the name ${name} already exists. Pick a different name or switch to that filter and update it.`
                        );
                        return;
                    }
                    this.loading = true;
                    let newFilter = this.filterSearchCriteriaInstance();
                    newFilter.name = name;
                    newFilter = this.addControlsAndItems(newFilter, true);
                    this.filterStateService.savePromise(newFilter).then((value) => {
                        this.selectedFilter = value;
                        this.filterStateList = this.filterStateList ? this.filterStateList : [];
                        this.filterStateList.push(value);
                        this.createFilterSelectQuestion();
                        this.storedFilterQuestion.input.control.setValue(value.name);
                        (this.storedFilterQuestion.input as SelectInputType).updateOptions = true;
                        this.loading = false;
                    });
                }
            });
    }

    removeFilters() {
        this.loading = true;
        this.filterStateService.deletePromise(this.selectedFilter.id).then((value) => {
            const index = this.filterStateList.map((m) => m.id).indexOf(this.selectedFilter.id);
            if (index >= 0) {
                this.filterStateList.splice(index, 1);
                if (this.filterStateList.length === 0) {
                    this.storedFilterQuestion = undefined;
                }
            }
            this.selectedFilter = null;
            if (this.storedFilterQuestion) {
                (this.storedFilterQuestion.input as SelectInputType).updateOptions = true;
                this.storedFilterQuestion.input.control.setValue(null);
            }
            this.loading = false;
        });
    }

    private processFilterChange(withSearch: boolean = true) {
        if (this.selectedFilter) {
            for (const k in this.controls) {
                const value = this.reflection.propertyByString(this.selectedFilter.controls, k);
                this.controls[k].setValue(value);
            }
            for (const item of this.selectedFilter.filters) {
                const filterItem = this.filterItems.find((m) => m.property === item.property);
                if (filterItem) {
                    filterItem.selectedOption = item.selectedOption;
                }
            }
            this.renderFilterOutput();
            if (withSearch) {
                this.search();
            }
        }
    }

    selectedFilterChange($event: QuestionValueChange) {
        if ($event.payload) {
            this.selectedFilter = this.filterStateList.find((m) => m.name == $event.payload);
            this.processFilterChange();
        } else {
            this.selectedFilter = undefined;
        }
    }

    updateFilters() {
        this.selectedFilter = this.addControlsAndItems(this.selectedFilter, true);
        this.loading = true;
        this.filterStateService.savePromise(this.selectedFilter).then((value) => {
            this.selectedFilter = value;
            const index = this.filterStateList.map((m) => m.id).indexOf(value.id);
            this.filterStateList[index] = this.selectedFilter;
            this.loading = false;
            this.renderFilterOutput();
            this.search();
        });
    }

    keydown($event: KeyboardEvent) {
        if ($event?.key === 'Enter') {
            this.search();
        }
    }

    filterThisItem(item: TableFilterItem, thisRef: any): boolean {
        return !item.lessCommon || (item.lessCommon === true && thisRef.controls.lessCommon.value === true);
    }

    onLessCommonChange($event: QuestionValueChange) {
        if ($event.payload !== true) {
            this.filterItems.forEach((item) => {
                if (item.lessCommon === true) {
                    this.controls[item.property].setValue(item.defaultValue);
                }
            });
        }
    }

    private addRemoveDirtyClass(isDirty: boolean) {
        if (this.searchButton && this.renderer2) {
            let button = this.searchButton.nativeElement;
            if (this.searchButton.nativeElement.nodeName === 'APP-BUTTON') {
                button = this.searchButton.nativeElement.querySelector('button');
            }
            if (!button) {
                return;
            }
            if (isDirty && !this.filtersMatch) {
                this.renderer2.removeClass(button, 'is-not-dirty');
                this.renderer2.addClass(button, 'is-dirty');
                this.renderer2.addClass(button, 'mat-elevation-z');
                this.searchButtonTooltip = 'Your filters have changed click to Search with your new filters.';
            } else {
                this.renderer2.removeClass(button, 'is-dirty');
                this.renderer2.addClass(button, 'is-not-dirty');
                this.renderer2.removeClass(button, 'mat-elevation-z');
                this.searchButtonTooltip = 'Click to Search.';
            }
        }
    }
}
